import { useTheme } from "../ThemeContext";
import { Badge } from "../ui/badge";
import { Zap } from "lucide-react";
import ShinyText from "../reactbits/ShinyText";
import {
    CarePlanGraphic,
    ChurnGraphic,
    GraphicBackdrop,
    MedicationGraphic,
    NoShowGraphic,
    OutreachGraphic,
    Stage,
} from "./ValueGraphics";

const features = [
    {
        name: "Reduce Patient Churn",
        description: "Accessible, hyper-personalized content keeps patients motivated and connected to their clinic between appointments",
        wide: true,
        graphic: <ChurnGraphic />,
        tint: "radial-gradient(closest-side, #a855f7, transparent)",
    },
    {
        name: "Reduce No-Show Rate",
        description: "AI-driven appointment reminders embedded directly into content keeps patients informed and proactive about their upcoming appointments",
        graphic: <NoShowGraphic />,
        tint: "radial-gradient(closest-side, #f59e0b, transparent)",
    },
    {
        name: "Increase Outreach",
        description: "Dynamic call-to-actions based on real-time patient profiling drives patient outreach and appointment scheduling when patients most need it",
        graphic: <OutreachGraphic />,
        tint: "radial-gradient(closest-side, #22d3ee, transparent)",
    },
    {
        name: "Medication Adherence",
        description: "Medication and prescription refill alerts embedded directly into content significantly improves protocol compliance",
        graphic: <MedicationGraphic />,
        tint: "radial-gradient(closest-side, #ec4899, transparent)",
    },
    {
        name: "Care Plan Adherence",
        description: "Care plan-informed personalized content between sessions leads to improved adherence and outcomes",
        graphic: <CarePlanGraphic />,
        tint: "radial-gradient(closest-side, #10b981, transparent)",
    },
];

export function QuiltFeatures() {
    const { theme } = useTheme();
    const dark = theme === "dark";

    return (
        <div className="w-full max-w-7xl mx-auto py-10 md:py-20 px-4">
            <div className="text-center mb-7 md:mb-16">
                <Badge variant="outline" className="mb-3 md:mb-4 px-4 py-2 bg-purple-500/10 border-purple-500/30">
                    <Zap className="w-4 h-4 mr-2 text-purple-500" />
                    <ShinyText text="Value" speed={3} className="font-medium text-purple-500" />
                </Badge>
                <h2 className={`text-3xl md:text-5xl font-bold mb-3 md:mb-6 ${dark ? "text-white" : "text-slate-900"}`}>
                    The Value of Patient Engagement
                </h2>
                <p className={`max-w-3xl mx-auto text-base md:text-lg ${dark ? "text-slate-400" : "text-slate-600"}`}>
                    We don’t just keep your patients engaged. We turn that engagement into value through better care continuity and retention.
                </p>
            </div>

            <div className="grid gap-4 md:gap-5 md:grid-cols-2 lg:grid-cols-3">
                {features.map((feature) => (
                    <article
                        key={feature.name}
                        className={`group relative flex flex-col overflow-hidden rounded-3xl border transition-all duration-500 hover:-translate-y-1 ${feature.wide ? "md:col-span-2" : ""} ${dark
                            ? "bg-slate-900/50 border-slate-800 hover:border-purple-500/40 hover:shadow-2xl hover:shadow-purple-500/10"
                            : "bg-white border-slate-200 hover:border-purple-300 hover:shadow-2xl hover:shadow-purple-500/10"
                            }`}
                    >
                        <Stage className="h-52 w-full md:h-60">
                            <GraphicBackdrop tint={feature.tint} />
                            <div className="relative h-full w-full px-3 pt-3 pb-1">{feature.graphic}</div>
                        </Stage>

                        <div className="flex flex-1 flex-col p-6 pt-3 pb-7">
                            <h3 className={`mb-2 text-xl font-semibold ${dark ? "text-white" : "text-slate-900"}`}>{feature.name}</h3>
                            <p className={`text-sm leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{feature.description}</p>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}

export default QuiltFeatures;
